import { useId, useState } from 'react'

// Minimal SVG chart primitives for the Performance pages. Single-series by
// design (no legend needed — the card title names the series); marks are thin
// with 4px rounded data-ends, the grid stays recessive, and every chart ships
// a hover tooltip.

// Vertical bar chart: data = [{ label, value, sub?, color? }]. Handles longish
// category labels by angling + truncating them (full name lives in the tooltip),
// so it works for both short time series and labeled category charts.
// `keyOf(d)` gives each bar a React key other than its label: a day series
// that crosses a year boundary repeats "Aug 22", and duplicate keys make React
// drop bars. `labelEvery` thins the x-axis labels — every bar is still drawn
// and hoverable, only every Nth label is printed. 'auto' picks N so at most
// ~24 labels appear; 1 (the default) prints all of them, as before.
// `labelOffset` PHASES that thinning: with labelEvery 3 and labelOffset 2 the
// printed labels are bars 2, 5, 8… instead of 0, 3, 6…. It exists because a
// thinned axis can leave the one bar the reader came for unlabelled — the
// busiest hour on the activity-density chart — and counting columns to find
// out which hour it is defeats the chart. 0 (the default) is the old
// behaviour exactly, so every existing caller is unchanged.
// `notYet: true` on a datum marks a bucket that HAS NOT HAPPENED YET (the
// hours after now on a chart of today). No rect is drawn for a 0, so those
// buckets would otherwise be blank space — pixel-identical to a bucket that
// happened and was empty, which is the one distinction a "when is this person
// working" chart cannot afford to lose. They get a faint hatched band across
// the plot instead. Callers that never set it are unchanged.
const MAX_AUTO_LABELS = 24

export function BarChart({
  data,
  height = 200,
  color = 'var(--primary)',
  formatValue = (v) => v.toLocaleString(),
  keyOf = null,
  labelEvery = 1,
  labelOffset = 0,
}) {
  const [hover, setHover] = useState(null) // index | null
  // Ids are document-global: three charts on one page sharing one pattern id
  // would all paint from whichever <defs> mounted last.
  const hatchId = `notyet-${useId().replace(/:/g, '')}`
  if (!data.length) return null

  const every =
    labelEvery === 'auto'
      ? Math.max(1, Math.ceil(data.length / MAX_AUTO_LABELS))
      : Math.max(1, Number(labelEvery) || 1)
  // Kept inside [0, every) with a double modulo so a negative or oversized
  // offset shifts the phase rather than printing no labels at all.
  const offset = ((Math.trunc(Number(labelOffset) || 0) % every) + every) % every

  // Wider viewBox than the container so labels/marks render smaller relative to
  // the card (a 720 box scaled ~2x made category labels look oversized).
  const width = 960
  // Angle the labels when there are many bars or any label is long.
  const angled = data.length > 6 || data.some((d) => String(d.label || '').length > 6)
  const pad = { top: 16, right: 8, bottom: angled ? 60 : 26, left: 8 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom
  const max = Math.max(1, ...data.map((d) => d.value))
  const step = innerW / data.length
  const barW = Math.min(40, step * 0.6)
  const baseY = pad.top + innerH

  const gridLines = [0.5, 1].map((f) => ({ y: pad.top + innerH * (1 - f), value: Math.round(max * f) }))
  const trunc = (s, n) => (s.length > n ? `${s.slice(0, n - 1)}…` : s)

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="block h-auto w-full"
        role="img"
        aria-label="Bar chart"
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {/* 45° hatch rather than a flat grey: a solid band reads as a bar of
              its own, and a hatch is the long-established "no data here yet"
              convention. Drawn at the same weight as the grid so it stays
              behind the marks. */}
          <pattern id={hatchId} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="7" className="stroke-line" strokeWidth="2" />
          </pattern>
        </defs>
        {gridLines.map((g) => (
          <g key={g.y}>
            <line x1={pad.left} x2={width - pad.right} y1={g.y} y2={g.y} className="stroke-line stroke-1 [stroke-dasharray:3_3]" />
            <text x={width - pad.right} y={g.y - 4} className="fill-faint text-[10px]" textAnchor="end">
              {g.value.toLocaleString()}
            </text>
          </g>
        ))}
        <line x1={pad.left} x2={width - pad.right} y1={baseY} y2={baseY} className="stroke-line stroke-1" />
        {data.map((d, i) => {
          const h = d.value === 0 ? 0 : Math.max(3, (d.value / max) * innerH)
          const x = pad.left + i * step + (step - barW) / 2
          const y = baseY - h
          const cx = pad.left + i * step + step / 2
          const labelY = baseY + (angled ? 12 : 16)
          const showLabel = i % every === offset
          return (
            <g key={keyOf ? keyOf(d) : d.label}>
              {d.notYet && (
                <rect
                  x={pad.left + i * step + 1}
                  y={pad.top}
                  width={Math.max(1, step - 2)}
                  height={innerH}
                  fill={`url(#${hatchId})`}
                  opacity={0.45}
                  pointerEvents="none"
                />
              )}
              {/* Oversized hit target so the tooltip is easy to reach. */}
              <rect x={pad.left + i * step} y={pad.top} width={step} height={innerH} fill="transparent" onMouseEnter={() => setHover(i)} />
              {d.value > 0 && (
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={h}
                  rx={4}
                  fill={d.color || color}
                  opacity={hover === null || hover === i ? 1 : 0.82}
                  className="transition-opacity duration-100 [transform-box:fill-box] [transform-origin:bottom] animate-[barGrow_0.55s_cubic-bezier(0.22,1,0.36,1)_both]"
                  style={{ animationDelay: `${Math.min(i * 28, 400)}ms` }}
                  pointerEvents="none"
                />
              )}
              {showLabel && (
                <text
                  x={cx}
                  y={labelY}
                  className="fill-faint text-[9px]"
                  textAnchor={angled ? 'end' : 'middle'}
                  transform={angled ? `rotate(-35 ${cx} ${labelY})` : undefined}
                >
                  {trunc(String(d.label), angled ? 12 : 12)}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      {hover !== null && (
        <div
          className="pointer-events-none absolute top-0 z-[5] -translate-x-1/2 whitespace-nowrap rounded-[8px] border border-[#333333] bg-[#151515] px-[10px] py-[6px] font-sora text-[12px] text-[#fafafa]"
          style={{ left: `${((pad.left + hover * step + step / 2) / width) * 100}%` }}
        >
          <div className="text-[11px] text-[rgba(255,255,255,0.7)]">
            {data[hover].tooltip || data[hover].label}
            {data[hover].sub ? ` · ${data[hover].sub}` : ''}
          </div>
          <div className="font-semibold">{formatValue(data[hover].value)}</div>
        </div>
      )}
    </div>
  )
}

// Horizontal labeled bar rows: data = [{ label, value, sub?, color? }].
// Identity lives in the row label (never color alone); values are always shown.
export function HBarList({ data, color = 'var(--primary)', formatValue = (v) => v.toLocaleString() }) {
  if (!data.length) return null
  const max = Math.max(1, ...data.map((d) => d.value))
  return (
    <div className="flex flex-col gap-y-[10px]">
      {data.map((d) => (
        <div className="grid grid-cols-[120px_1fr_56px] items-center gap-x-[12px] min-[721px]:grid-cols-[220px_1fr_64px]" key={d.label} title={`${d.label}: ${formatValue(d.value)}`}>
          <div className="overflow-hidden text-ellipsis whitespace-nowrap font-sora text-[13px] font-medium text-ink">
            {d.label}
            {d.sub && <span className="ml-[6px] text-[12px] font-normal text-dim">{d.sub}</span>}
          </div>
          <div className="h-[14px] overflow-hidden rounded-[4px] bg-panel">
            <div
              className="h-full min-w-[3px] rounded-[4px]"
              style={{
                width: `${Math.max(1.5, (d.value / max) * 100)}%`,
                background: d.color || color,
              }}
            />
          </div>
          <div className="text-right text-[12.5px] tabular-nums text-dim">{formatValue(d.value)}</div>
        </div>
      ))}
    </div>
  )
}
